// src/pages/admin/guardias/GuardiasCalendarPage.tsx
import { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Button,
  Chip,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { ChevronLeft, ChevronRight, CalendarMonth } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";

import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { useTheme } from "@mui/material/styles";

import { useGuardiasCuadros, useGuardiasTurnos } from "../../../hooks/useGuardias";
import { useCentros } from "../../../hooks/useCentros";
import AsignacionesDrawer from "../../../components/guardias/AsignacionesDrawer";
import TurnoInfoDialog from "../../../components/guardias/TurnoInfoDialog";
import { useAuth } from "../../../context/AuthContext";

/* helpers */

const hhmm = (s?: string | null) => (s ? String(s).slice(0, 5) : "");
const toStart = (fecha: string, hm: string) => `${fecha}T${hm}:00`;

/** Obtiene nombres asignados a un turno (string[]) con fallback a relaciones */
function nombresAsignados(t: any): string[] {
  if (Array.isArray(t?.asignados) && t.asignados.length) {
    return t.asignados
      .map((x: any) =>
        typeof x === "string"
          ? x
          : x?.nombreCompleto || x?.email || (x?.id != null ? `#${x.id}` : null)
      )
      .filter(Boolean);
  }
  const rel = (t?.asignaciones ?? [])
    .filter((a: any) => a?.estado === "asignado")
    .map((a: any) => a?.usuario?.nombreCompleto || a?.usuario?.email)
    .filter(Boolean);
  return rel;
}

export default function GuardiasCalendarPage() {
  const theme = useTheme();
  const { user } = useAuth();
  const isProfesor = !!user?.roles?.includes("profesor");

  // ── mes visible ──
  const [month, setMonth] = useState(dayjs().startOf("month"));
  const y = month.year();
  const m = month.month() + 1;

  // ── cuadros ──
  const {
    rows: cuadros,
    loading: loadingC,
    refresh: refCuadros,
    create: createCuadro,
  } = useGuardiasCuadros();
  useEffect(() => {
    refCuadros(); /* eslint-disable-next-line */
  }, []);
  const cuadroActual = useMemo(
    () => cuadros.find((c) => c.anio === y && c.mes === m),
    [cuadros, y, m]
  );

  // ── turnos ──
  const {
    rows: turnos,
    loading: loadingT,
    refresh: refTurnos,
  } = useGuardiasTurnos(cuadroActual?.id);
  useEffect(() => {
    if (cuadroActual) refTurnos(); /* eslint-disable-next-line */
  }, [cuadroActual?.id]);

  // ── centros ──
  const { rows: centros = [] } = useCentros();
  const centrosMap = useMemo(
    () => new Map(centros.map((c: any) => [Number(c.id), c.nombre])),
    [centros]
  );

  // ── drawer asignaciones & diálogo info ──
  const [drawer, setDrawer] = useState<{
    open: boolean;
    turno: { id: number; capacidad: number } | null;
  }>({ open: false, turno: null });
  const [info, setInfo] = useState<any | null>(null);

  // ── calendario ──
  const calRef = useRef<FullCalendar>(null);
  const goto = (delta: number) => {
    const next = month.add(delta, "month");
    setMonth(next.startOf("month"));
    calRef.current?.getApi().gotoDate(next.toDate());
  };

  const crearCuadroMes = async () => {
    if (cuadroActual) return;
    await createCuadro({ anio: y, mes: m });
    await refCuadros();
  };

  const handleDatesSet = (arg: any) => {
    const visibleMonth = dayjs(arg.start).add(15, "day").startOf("month");
    setMonth(visibleMonth);
  };

  /* eventos */
  const events = useMemo(() => {
    return (turnos as any[]).map((t) => {
      const color =
        t.estado === "publicado"
          ? theme.palette.success.main
          : t.estado === "cancelado"
            ? theme.palette.warning.main
            : theme.palette.info.main;

      const nombres = nombresAsignados(t);
      const nombresLabel = nombres.length ? nombres.join(", ") : "Sin asignar";
      const cellLabel = `${hhmm(t.horaInicio)}–${hhmm(t.horaFin)} · ${nombresLabel}`;

      // nombre de centro resuelto por id (solo si Presencial)
      const centroNombre =
        Number(t.formatoId) === 2 ? (centrosMap.get(Number(t.centroId)) ?? "—") : null;

      const tooltip = [
        `Fecha: ${t.fecha}`,
        `Horario: ${hhmm(t.horaInicio)}–${hhmm(t.horaFin)}`,
        `Formato: ${Number(t.formatoId) === 2 ? "Presencial" : "Online"}`,
        centroNombre ? `Centro: ${centroNombre}` : null,
        `Capacidad: ${t.capacidad}`,
        `Estado: ${t.estado}`,
      ]
        .filter(Boolean)
        .join("\n");

      return {
        id: String(t.id),
        start: toStart(t.fecha, hhmm(t.horaInicio)),
        end: toStart(t.fecha, hhmm(t.horaFin)),
        title: cellLabel,
        backgroundColor: "#21a598", // color forzado
        borderColor: "#21a598", // color forzado
        textColor: "#fff", // opcional, para que se vea
        extendedProps: { turnoRaw: t, tooltip },
        display: "block",
      } as any;
    });
  }, [turnos, theme.palette, centrosMap]);

  // Turnos por fecha → tooltip celda
  const turnosPorFecha = useMemo(() => {
    const map = new Map<string, any[]>();
    for (const t of turnos as any[]) {
      const list = map.get(t.fecha) ?? [];
      list.push(t);
      map.set(t.fecha, list);
    }
    return map;
  }, [turnos]);

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" spacing={1} alignItems="center" mb={2}>
        <IconButton onClick={() => goto(-1)}>
          <ChevronLeft />
        </IconButton>
        <Typography variant="h6" sx={{ textTransform: "capitalize" }}>
          {month.format("MMMM YYYY")}
        </Typography>
        <IconButton onClick={() => goto(+1)}>
          <ChevronRight />
        </IconButton>

        <Box sx={{ flexGrow: 1 }} />

        {/* ⬇️ Ocultar para PROFESOR: botón CUADRO, crear cuadro y chip de estado */}
        {!isProfesor && (
          <Tooltip title="Vista cuadro (fines de semana)">
            <Button
              variant="text"
              size="small"
              startIcon={<CalendarMonth />}
              component={RouterLink}
              to="/admin/manager/guardias"
            >
              Cuadro
            </Button>
          </Tooltip>
        )}

        {!isProfesor && !cuadroActual && (
          <Button variant="contained" onClick={crearCuadroMes} disabled={loadingC}>
            Crear cuadro del mes
          </Button>
        )}

        {!isProfesor && cuadroActual && (
          <Chip
            label={`Estado: ${cuadroActual.estado}`}
            color={
              cuadroActual.estado === "publicado"
                ? "success"
                : cuadroActual.estado === "archivado"
                  ? "warning"
                  : "default"
            }
          />
        )}
      </Stack>

      {(loadingC || loadingT) && <CircularProgress size={22} />}

      {/* Calendario mensual */}
      <FullCalendar
        ref={calRef}
        plugins={[dayGridPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        initialDate={month.toDate()}
        height="auto"
        headerToolbar={false}
        firstDay={1}
        weekends
        dayMaxEventRows={3}
        displayEventTime={false}
        events={events}
        datesSet={handleDatesSet}
        eventClick={(arg: any) => {
          const t: any = arg.event.extendedProps?.turnoRaw;
          if (!t) return;
          // añadimos nombre de centro resuelto para que el diálogo lo pueda mostrar aunque no tenga `nombreCentro`
          const withCentro = {
            ...t,
            nombreCentro:
              Number(t.formatoId) === 2 ? (centrosMap.get(Number(t.centroId)) ?? "—") : "—",
          };
          setInfo(withCentro);
        }}
        eventDidMount={(info: any) => {
          const tip: string | undefined = info.event.extendedProps?.tooltip;
          if (tip) info.el.setAttribute("title", tip);
        }}
        dayCellDidMount={(arg: any) => {
          const dateStr = dayjs(arg.date).format("YYYY-MM-DD");
          const list = turnosPorFecha.get(dateStr) || [];
          if (!list.length) return;
          const lines = list.map((t) => {
            const centroLabel =
              Number(t.formatoId) === 2
                ? (centrosMap.get(Number(t.centroId)) ?? "Presencial")
                : "Online";
            return `• ${hhmm(t.horaInicio)}-${hhmm(t.horaFin)} · ${centroLabel} · Cap ${t.capacidad}`;
          });
          const tip = [`${dayjs(dateStr).format("dddd D MMM")}`, ...lines].join("\n");
          arg.el.setAttribute("title", tip);
        }}
      />

      {/* Diálogo info */}
      <TurnoInfoDialog
        open={!!info}
        turno={info}
        onClose={() => setInfo(null)}
        onOpenAsignaciones={(turnoId, capacidad) =>
          setDrawer({ open: true, turno: { id: turnoId, capacidad } })
        }
        onChanged={refTurnos}
      />

      {/* Drawer asignaciones */}
      <AsignacionesDrawer
        open={drawer.open}
        onClose={() => setDrawer({ open: false, turno: null })}
        turno={drawer.turno}
      />
    </Box>
  );
}
